import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { logEvent } from 'firebase/analytics';
import { db, analytics } from '../api/firebase';
import Fuse from 'fuse.js';
import {
  TextField,
  Autocomplete,
  Box,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const LecturerSearch = ({ universityId }) => {
  const [lecturers, setLecturers] = useState([]);
  const [departments, setDepartments] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isAdmin } = useAuth();

  // Security limit: Maximum 4 results
  const MAX_SEARCH_RESULTS = 4;

  useEffect(() => {
    const fetchLecturers = async () => {
      if (!universityId) return;
      
      setLoading(true);
      try {
        // Fetch all lecturers for this university
        const lecturersQuery = query(
          collection(db, 'professors'),
          where('universityId', '==', universityId)
        );
        const profSnapshot = await getDocs(lecturersQuery);
        const profsList = profSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }));
        setLecturers(profsList);

        // Fetch departments to show department names
        const departmentsQuery = query(
          collection(db, 'departments'),
          where('universityId', '==', universityId)
        );
        const deptSnapshot = await getDocs(departmentsQuery);
        const deptsMap = {};
        deptSnapshot.docs.forEach(doc => {
          deptsMap[doc.id] = doc.data().name;
        });
        setDepartments(deptsMap);

      } catch (error) {
        console.error('Error fetching lecturers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLecturers();
  }, [universityId]);

  // Configure Fuse.js for fuzzy search (English + Arabic + transliteration)
  const fuse = useMemo(() => {
    return new Fuse(lecturers, {
      keys: [
        { name: 'name', weight: 2 },           // English name (highest priority)
        { name: 'full_name', weight: 2 },      // Alternative name field
        { name: 'nameArabic', weight: 2 },     // Arabic name (SAME WEIGHT as English!)
        { name: 'searchKeywords', weight: 1.5 }, // Transliteration keywords (e.g., "ahmad" for "أحمد")
      ],
      threshold: 0.4,           // 0 = exact match, 1 = match anything (0.4 = moderate fuzzy)
      distance: 100,            // Maximum distance to search
      minMatchCharLength: 2,    // Minimum 2 characters to match
      includeScore: true,       // Include match score for sorting
      ignoreLocation: true,     // Search anywhere in the string
      useExtendedSearch: false,
    });
  }, [lecturers]);

  // Filter and limit search results (with fuzzy matching)
  const filteredLecturers = useMemo(() => {
    if (!inputValue || inputValue.length < 2) {
      return []; // Don't show results until user types at least 2 characters
    }
    
    // Use Fuse.js for fuzzy search
    const results = fuse.search(inputValue);
    
    // Extract the lecturer objects and limit results
    const filtered = results
      .map(result => result.item)
      .slice(0, MAX_SEARCH_RESULTS);
    
    return filtered;
  }, [fuse, inputValue, MAX_SEARCH_RESULTS]);

  const handleLecturerSelect = (event, lecturer) => {
    if (lecturer) {
      // Track lecturer selection from search
      logEvent(analytics, 'select_lecturer_from_search', {
        lecturer_id: lecturer.id,
        lecturer_name: lecturer.name || lecturer.full_name,
        search_term: inputValue,
        university_id: universityId
      });
      
      setSearchLoading(true);
      // Navigate to lecturer page
      navigate(`/lecturer/${lecturer.id}`);
    }
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    
    // Track search queries (only for meaningful searches)
    if (newInputValue.length >= 2) {
      // Use fuzzy search to count results
      const searchResults = fuse.search(newInputValue);
      
      logEvent(analytics, 'search_lecturers', {
        search_term: newInputValue,
        result_count: Math.min(searchResults.length, MAX_SEARCH_RESULTS),
        university_id: universityId
      });
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SearchIcon />
        {t('Search Lecturers', 'البحث عن المحاضرين')}
      </Typography>
      
      <Autocomplete
        options={filteredLecturers}
        filterOptions={(x) => x}
        getOptionLabel={(option) => option.name || option.full_name || ''}
        loading={loading || searchLoading}
        onChange={handleLecturerSelect}
        onInputChange={handleInputChange}
        inputValue={inputValue}
        freeSolo
        noOptionsText={
          inputValue.length < 2 
            ? t("Type at least 2 characters to search...", "اكتب حرفين على الأقل للبحث...")
            : loading 
              ? t("Loading lecturers...", "جاري تحميل المحاضرين...")
              : (
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t("No lecturers found", "لم يتم العثور على محاضرين")}
                    {isAdmin && (
                      <>
                        {" - "}
                        <Link 
                          to="/add-lecturer" 
                          style={{ 
                            textDecoration: 'underline',
                            color: 'inherit',
                            fontWeight: 'bold'
                          }}
                        >
                          {t("Add them", "أضفهم")}
                        </Link>
                      </>
                    )}
                  </Typography>
                </Box>
              )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t('Search for a lecturer by name (min. 2 characters)...', 'البحث عن محاضر بالاسم (حرفين على الأقل)...')}
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
              }
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {option.name || option.full_name}
              </Typography>
              {departments[option.departmentId] && (
                <Chip 
                  label={departments[option.departmentId]} 
                  size="small" 
                  variant="outlined"
                  sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                />
              )}
            </Box>
          </Box>
        )}
        sx={{ maxWidth: 600 }}
      />
      
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {inputValue.length >= 2 && filteredLecturers.length > 0 && (
          <>{t('Showing', 'عرض')} {filteredLecturers.length} {t('of', 'من')} {fuse.search(inputValue).length} {t('matching lecturers', 'محاضر مطابق')}</>
        )}
        {inputValue.length >= 2 && filteredLecturers.length === 0 && (
          <>{t('No lecturers found matching', 'لم يتم العثور على محاضر مطابقين لـ')} "{inputValue}"</>
        )}
        {inputValue.length < 2 && (
          <>{lecturers.length} {t('lecturers available to search', 'محاضر متاح للبحث')}</>
        )}
      </Typography>
    </Box>
  );
};

export default LecturerSearch; 